import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canAccessUsersModule, getRoleLabel } from '../../utils/permissions';
import { dashboardService } from '../../services/dashboardService';
import { useLiveEvents } from '../../utils/liveEvents';
import {
  ActivityChart,
  CoverageMeter,
  PhoneDots,
  RankedBars,
  Sparkline,
} from './DashboardCharts';
import {
  auditPlace,
  buildAttention,
  buildDaily,
  buildHourlyToday,
  buildSparkline,
  coverage,
  formatNumber,
  formatRemaining,
  formatStart,
  SYNC_LABELS,
} from './dashboardData';
import './Dashboard.css';
import { PageLoader } from '../../components/Loader';
import { ScheduleKpiBoard } from '../../components/ScheduleKpis';
import { auditService } from '../../services/auditService';

// Every 20 minutes (requested), plus whenever the tab is shown again or Refresh is pressed.
const REFRESH_MS = 20 * 60 * 1000;
// When phones upload scans (live stream), refresh at most this often.
const LIVE_REFRESH_GAP_MS = 2 * 60 * 1000;

function useDashboardSummary(enabled) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(enabled);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await dashboardService.getSummary();
      setSummary(data);
      setError('');
      setUpdatedAt(new Date());
    } catch (err) {
      setError(err.message || 'Could not load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();

    // Refresh while the tab is visible; catch up as soon as it's shown again.
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, load]);

  // Live stream: new scans / recounts / sign-offs refresh the numbers, but
  // no more than once every LIVE_REFRESH_GAP_MS and only while visible.
  const live = useLiveEvents(
    enabled ? '/audits/live/events' : null,
    () => {
      if (document.visibilityState === 'visible') load();
    },
    { debounceMs: LIVE_REFRESH_GAP_MS, types: ['scans', 'recount', 'signoff'] },
  );

  return { summary, error, loading, updatedAt, reload: load, live };
}

/** Schedules for the KPI board: all schedules for staff, their own for clients. */
function useKpiSchedules(isClient) {
  const [schedules, setSchedules] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (isClient ? auditService.getClientAudits() : auditService.getAll())
      .then((list) => {
        if (!cancelled) setSchedules(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSchedules([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isClient]);
  return schedules;
}

function Dashboard() {
  const { user } = useAuth();

  if (!canAccessUsersModule(user)) {
    return <WelcomeCard user={user} />;
  }

  return <OperationsDashboard user={user} />;
}

function OperationsDashboard({ user }) {
  const { summary, error, loading, updatedAt, reload } = useDashboardSummary(true);
  const kpiSchedules = useKpiSchedules(false);
  const [range, setRange] = useState('today');
  const [refreshing, setRefreshing] = useState(false);

  const attention = useMemo(() => buildAttention(summary), [summary]);
  const hourly = useMemo(
    () => (summary ? buildHourlyToday(summary.activity, summary.now_local) : []),
    [summary],
  );
  const daily = useMemo(
    () => (summary ? buildDaily(summary.activity, summary.now_local) : []),
    [summary],
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="page ops">
      <header className="ops__header">
        <div>
          <p className="page__eyebrow">Overview</p>
          <h1>{firstName ? `Good to see you, ${firstName}` : 'Dashboard'}</h1>
          <p className="ops__subtitle">
            What&rsquo;s happening on the floor right now, and what needs you.
          </p>
        </div>
        <div className="ops__refresh">
          {updatedAt ? (
            <span>
              Updated{' '}
              {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' '}· refreshes every 20 min
            </span>
          ) : null}
          <button type="button" onClick={handleRefresh} disabled={refreshing || loading}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <ScheduleKpiBoard schedules={kpiSchedules} />

      {error ? (
        <div className="ops__alert" role="alert">
          {summary
            ? `Couldn’t refresh: ${error}. Showing the last data loaded.`
            : `Couldn’t load the dashboard: ${error}`}
        </div>
      ) : null}

      {loading && !summary ? (
        <PageLoader label="Loading live schedules…" />
      ) : summary ? (
        <>
          <StatStrip summary={summary} attentionCount={attention.length} />
          <LiveFloor summary={summary} />

          <div className="ops__grid ops__grid--wide-left">
            <section className="ops-panel" aria-labelledby="activity-title">
              <header className="ops-panel__header">
                <div>
                  <h2 id="activity-title">Scans received</h2>
                  <p>
                    {range === 'today'
                      ? 'Per hour today, by the time each item was scanned'
                      : 'Per day, last 14 days'}
                  </p>
                </div>
                <div className="segmented" role="group" aria-label="Time range">
                  <button
                    type="button"
                    aria-pressed={range === 'today'}
                    onClick={() => setRange('today')}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    aria-pressed={range === '14d'}
                    onClick={() => setRange('14d')}
                  >
                    14 days
                  </button>
                </div>
              </header>
              <ActivityChart
                points={range === 'today' ? hourly : daily}
                labelEvery={range === 'today' ? 3 : 2}
                emptyText={
                  range === 'today'
                    ? 'No scans received today yet. Phones in the field upload as soon as they get signal.'
                    : 'No scans in the last 14 days.'
                }
              />
            </section>

            <AttentionPanel items={attention} />
          </div>

          <div className="ops__grid ops__grid--wide-right">
            <section className="ops-panel" aria-labelledby="scanners-title">
              <header className="ops-panel__header">
                <div>
                  <h2 id="scanners-title">Top scanners today</h2>
                  <p>Scans received per person</p>
                </div>
              </header>
              {summary.top_scanners?.length ? (
                <RankedBars rows={summary.top_scanners} />
              ) : (
                <p className="ops-panel__empty">
                  Nobody has synced scans today yet.
                </p>
              )}
            </section>

            <UpcomingPanel audits={summary.upcoming || []} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function StatStrip({ summary, attentionCount }) {
  const t = summary.totals || {};
  const stats = [
    {
      label: 'Schedules today',
      value: formatNumber(t.today_audits ?? t.live_audits),
      note: `${formatNumber(t.live_audits)} in progress now · ${formatNumber(t.upcoming_audits)} in the next 7 days`,
    },
    {
      label: 'Valid scans today',
      value: formatNumber(t.scans_today),
      note: `${formatNumber(t.units_today)} units counted${Number(t.invalid_today) > 0 ? ` · ${formatNumber(t.invalid_today)} invalid` : ''}`,
    },
    {
      label: 'Phones synced in the last hour',
      value: formatNumber(t.phones_last_hour),
      note: `${formatNumber(t.phones_today)} active today · ${formatNumber(t.registered_phones)} registered`,
    },
    {
      label: 'Needs attention',
      value: formatNumber(attentionCount),
      note: attentionCount ? 'See the list below' : 'Nothing needs you right now',
      tone: attentionCount ? 'alert' : 'calm',
    },
  ];

  return (
    <dl className="stat-strip">
      {stats.map((stat) => (
        <div key={stat.label} className={`stat-strip__item stat-strip__item--${stat.tone || 'plain'}`}>
          <dt>{stat.label}</dt>
          <dd>
            <span className="stat-strip__value">{stat.value}</span>
            <span className="stat-strip__note">{stat.note}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

const FLOOR_FILTERS = [
  { id: 'all', label: 'All today' },
  { id: 'live', label: 'In progress' },
  { id: 'upcoming', label: 'Later today' },
  { id: 'ended', label: 'Finished' },
];

const STATUS_PILL = {
  live: 'In progress',
  upcoming: 'Later today',
  ended: 'Finished',
};

function clockTime(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** "3 h 10 min left" / "Starts 14:30" / "Ended 13:00". */
function statusTime(audit) {
  if (audit.status === 'upcoming') return `Starts ${clockTime(audit.start_at)}`;
  if (audit.status === 'ended') return `Ended ${clockTime(audit.end_at)}`;
  return formatRemaining(audit.end_at);
}

/** Today's audits: in progress, later today and finished today, with progress. */
function LiveFloor({ summary }) {
  const today = summary.today || summary.live || [];
  const [filter, setFilter] = useState('all');
  const counts = useMemo(
    () => ({
      all: today.length,
      live: today.filter((a) => a.status === 'live').length,
      upcoming: today.filter((a) => a.status === 'upcoming').length,
      ended: today.filter((a) => a.status === 'ended').length,
    }),
    [today],
  );
  const shown = filter === 'all' ? today : today.filter((a) => a.status === filter);

  return (
    <section className="live-floor" aria-labelledby="live-floor-title">
      <header className="live-floor__header">
        <div>
          <h2 id="live-floor-title">
            <span
              className={`live-floor__pulse${counts.live ? '' : ' is-idle'}`}
              aria-hidden="true"
            />
            Today&rsquo;s schedules
          </h2>
          <p>
            {today.length
              ? `${today.length} schedule${today.length === 1 ? '' : 's'} today · ${counts.live} in progress · ${counts.upcoming} later · ${counts.ended} finished`
              : 'No schedules today'}
          </p>
        </div>
        <ul className="live-floor__legend" aria-label="Phone sync legend">
          <li><span className="phone-dot phone-dot--fresh" aria-hidden="true"><span /></span>{SYNC_LABELS.fresh}</li>
          <li><span className="phone-dot phone-dot--late" aria-hidden="true"><span /></span>{SYNC_LABELS.late}</li>
          <li><span className="phone-dot phone-dot--stale" aria-hidden="true"><span /></span>{SYNC_LABELS.stale}</li>
        </ul>
      </header>

      {today.length > 0 ? (
        <div className="live-floor__filters" role="group" aria-label="Show">
          {FLOOR_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
              disabled={item.id !== 'all' && counts[item.id] === 0}
            >
              {item.label} <span>{counts[item.id]}</span>
            </button>
          ))}
        </div>
      ) : null}

      {today.length === 0 ? (
        <div className="live-floor__empty">
          <p>Today’s schedules appear here with each team&rsquo;s progress.</p>
          <Link to="/audits">Create a schedule</Link>
        </div>
      ) : (
        <div className="live-floor__rows" role="list">
          <div className="live-floor__cols" aria-hidden="true">
            <span>Schedule</span>
            <span>Product list covered</span>
            <span>Scanned today</span>
            <span>Team &amp; phones</span>
            <span />
          </div>
          {shown.map((audit) => {
            const cov = coverage(audit);
            const spark = buildSparkline(audit.hourly, summary.now_local);
            return (
              <article key={audit.id} className={`live-row live-row--${audit.status}`} role="listitem">
                <div className="live-row__audit">
                  <h3>
                    {audit.name}
                    <span className={`live-row__status live-row__status--${audit.status}`}>
                      {STATUS_PILL[audit.status] || 'In progress'}
                    </span>
                  </h3>
                  <p>
                    {auditPlace(audit) || 'No outlet set'}
                    <span className="live-row__time">{statusTime(audit)}</span>
                  </p>
                </div>

                <div className="live-row__coverage">
                  {cov.total > 0 ? (
                    <>
                      <div className="live-row__coverage-top">
                        <strong>{cov.percent}%</strong>
                        <span>
                          {formatNumber(cov.found)} of {formatNumber(cov.total)} products
                        </span>
                      </div>
                      <CoverageMeter percent={cov.percent} />
                    </>
                  ) : (
                    <span className="live-row__warn">No product list uploaded</span>
                  )}
                </div>

                <div className="live-row__pace">
                  <div className="live-row__pace-numbers">
                    <strong>{formatNumber(audit.scans_today)}</strong>
                    <span className="live-row__pace-note">
                      valid scans · {formatNumber(audit.units_today)} units
                      <br />
                      {Number(audit.invalid_today) > 0
                        ? `${formatNumber(audit.invalid_today)} invalid · `
                        : ''}
                      {formatNumber(audit.scans_last_hour)} in the last hour
                    </span>
                  </div>
                  {audit.status !== 'upcoming' ? (
                    <Sparkline
                      values={spark}
                      label={`Scans per hour over the last 8 hours: ${spark.join(', ')}`}
                    />
                  ) : null}
                </div>

                <div className="live-row__phones">
                  <span className="live-row__people">
                    {Number(audit.member_count) > 0
                      ? `${formatNumber(audit.people_today)} of ${formatNumber(audit.member_count)} team members scanned today`
                      : `${formatNumber(audit.people_today)} people scanned today · no team assigned`}
                  </span>
                  <PhoneDots phones={audit.phones || []} />
                </div>

                <div className="live-row__action">
                  <Link to={`/audits/${audit.id}/portal`}>Open portal</Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

const SEVERITY_TEXT = { critical: 'Blocking', warning: 'Check' };
const SEVERITY_ICON = { critical: '!', warning: '?' };

const ATTENTION_PREVIEW = 3;

function AttentionPanel({ items }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, ATTENTION_PREVIEW);
  const hidden = items.length - visible.length;

  return (
    <section className="ops-panel" aria-labelledby="attention-title">
      <header className="ops-panel__header">
        <div>
          <h2 id="attention-title">Needs attention</h2>
          <p>Setup gaps and phones that have gone quiet</p>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="attention__clear">
          <span className="attention__clear-icon" aria-hidden="true">✓</span>
          <p>All live and upcoming schedules are set up, and every phone has synced recently.</p>
        </div>
      ) : (
        <ul className="attention">
          {visible.map((item) => (
            <li key={item.id} className={`attention__item attention__item--${item.severity}`}>
              <span className="attention__badge">
                <span aria-hidden="true">{SEVERITY_ICON[item.severity]}</span>
                {SEVERITY_TEXT[item.severity]}
              </span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
                <Link to={item.to}>{item.action}</Link>
              </div>
            </li>
          ))}
          {hidden > 0 || expanded ? (
            <li className="attention__more">
              <button type="button" onClick={() => setExpanded((value) => !value)}>
                {expanded ? 'Show fewer' : `Show all ${items.length}`}
              </button>
            </li>
          ) : null}
        </ul>
      )}
    </section>
  );
}

function UpcomingPanel({ audits }) {
  return (
    <section className="ops-panel" aria-labelledby="upcoming-title">
      <header className="ops-panel__header">
        <div>
          <h2 id="upcoming-title">Coming up</h2>
          <p>Next 7 days</p>
        </div>
        <Link className="ops-panel__link" to="/audits">All schedules</Link>
      </header>
      {audits.length === 0 ? (
        <p className="ops-panel__empty">Nothing scheduled for the next 7 days.</p>
      ) : (
        <ul className="upcoming">
          {audits.slice(0, 6).map((audit) => {
            const when = formatStart(audit.start_at);
            return (
              <li key={audit.id} className="upcoming__item">
                <time className="upcoming__when" dateTime={audit.start_at}>
                  <span>{when.day}</span>
                  <strong>{when.time}</strong>
                </time>
                <div className="upcoming__body">
                  <strong>{audit.name}</strong>
                  <span>{auditPlace(audit) || 'No outlet set'}</span>
                </div>
                <div className="upcoming__checks">
                  <span className={audit.has_master_file ? 'check check--ok' : 'check check--missing'}>
                    <span aria-hidden="true">{audit.has_master_file ? '✓' : '!'}</span>
                    {audit.has_master_file
                      ? `${formatNumber(audit.product_count)} products`
                      : 'No product list'}
                  </span>
                  <span className={audit.team_count ? 'check check--ok' : 'check check--missing'}>
                    <span aria-hidden="true">{audit.team_count ? '✓' : '!'}</span>
                    {audit.team_count
                      ? `${formatNumber(audit.member_count)} people`
                      : 'No team'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Non-management users keep the simple profile view. */
function WelcomeCard({ user }) {
  const isClient = user?.role_name === 'client';
  const kpiSchedules = useKpiSchedules(isClient);
  return (
    <div className="page dashboard">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">Overview</p>
          <h1>Dashboard</h1>
        </div>
      </header>

      {isClient ? <ScheduleKpiBoard schedules={kpiSchedules} storageKey="clientKpiSchedule" /> : null}

      <section className="dashboard__card">
        <div className="dashboard__profile">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name || user.email} />
          ) : (
            <div className="dashboard__avatar-fallback">
              {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
            </div>
          )}

          <div>
            <h2>{user?.name || 'Admin User'}</h2>
            <p>{user?.email}</p>
          </div>
        </div>

        <div className="dashboard__meta">
          <div>
            <span>User ID</span>
            <strong>{user?.id}</strong>
          </div>
          <div>
            <span>Role</span>
            <strong>{getRoleLabel(user)}</strong>
          </div>
          <div>
            <span>Provider</span>
            <strong>{user?.provider || 'google'}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong className="dashboard__status">Authenticated</strong>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Dashboard;
